import { useEffect, useRef, useState } from 'react';
import styles from './MessageTile.module.css';
import { normalizeProjectScopedCtaUrl } from '../../utils/projectPath';

/**
 * Type/sentiment -> hex color mapping (TRD:
 * project-global-messages-trd.md, Section 4.1 Requirement 5). Kept in
 * sync with the --gm-color-* custom properties declared in
 * GlobalMessages.module.css's `:root` block -- those are the discoverable
 * named design tokens; this literal copy exists only because CSS can't
 * derive an R,G,B triplet from a hex custom property (needed for the
 * shadow's reduced-opacity rgba()) without color-mix()/relative-color
 * syntax. ERROR is fixed regardless of `sentiment` (Acceptance Criterion
 * 10) -- sentiment is only consulted for ALERT/ANNOUNCEMENT.
 */
const GM_TILE_COLORS = {
  ERROR: '#C85C5C',
  ALERT_NEGATIVE: '#D4A017',
  ALERT_POSITIVE: '#A8D5A2',
  ANNOUNCEMENT_POSITIVE: '#D4AF37',
  ANNOUNCEMENT_NEGATIVE: '#6B2737',
};

function resolveTileColor(type, sentiment) {
  if (type === 'ERROR') return GM_TILE_COLORS.ERROR;
  if (type === 'ALERT') {
    return sentiment === 'POSITIVE' ? GM_TILE_COLORS.ALERT_POSITIVE : GM_TILE_COLORS.ALERT_NEGATIVE;
  }
  if (type === 'ANNOUNCEMENT') {
    return sentiment === 'POSITIVE' ? GM_TILE_COLORS.ANNOUNCEMENT_POSITIVE : GM_TILE_COLORS.ANNOUNCEMENT_NEGATIVE;
  }
  // Defensive fallback for an unrecognized type -- should never happen
  // given the backend's ENUM constraint, but avoids an unstyled tile.
  return GM_TILE_COLORS.ALERT_NEGATIVE;
}

function hexToRgbTriplet(hex) {
  const clean = hex.replace('#', '');
  const value = parseInt(clean, 16);
  return `${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}`;
}

/**
 * One project-wide message row (TRD:
 * project-global-messages-trd.md, Section 4.1 Requirement 4). Collapsed
 * by default: centered text clamped to 1 line (2 below the 768px
 * breakpoint AppLayout.module.css already uses), a Read More/Read Less
 * affordance when the text actually overflows the clamp, a Dismiss action
 * always present, and an optional positive CTA link when the message
 * carries both cta_label and cta_url.
 *
 * @param {object} message - one entry from GET
 *   /project/:projectId/messages's data.messages array: { id, type,
 *   sentiment, message, cta_label, cta_url, expiry_at, created_at }.
 * @param {(messageId: string) => void} onDismissClick - called when the
 *   user clicks Dismiss; the parent (GlobalMessages) owns the actual
 *   POST .../dismiss call + optimistic list update.
 * @param {string|null|undefined} currentProjectId - the active project's
 *   id, used to safety-net-normalize message.cta_url before it's rendered
 *   as an href (docs/product/bugs/open/new-tab-link-wrong-project-unscoped-cta-url/trd.md,
 *   Bounded Unit 2).
 */
const MessageTile = ({ message, onDismissClick, currentProjectId }) => {
  const messageRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Truncation detection mirrors InsightTile.jsx's checkTruncation
  // (scrollHeight-vs-clientHeight against the line-clamped element,
  // re-checked via ResizeObserver). Skipped entirely while expanded: once
  // the clamp is removed there's nothing left to overflow, and
  // recomputing here would flip isTruncated back to false and hide the
  // Read Less affordance the user just revealed -- so the last
  // collapsed-state truncation reading intentionally stays in place until
  // the tile collapses again (isExpanded is a dependency, so collapsing
  // re-runs this and gets a fresh reading). jsdom (unit tests) has no
  // ResizeObserver -- the check still runs once on mount, it just won't
  // react to later resizes in that environment.
  useEffect(() => {
    if (isExpanded) return undefined;
    const el = messageRef.current;
    if (!el) return undefined;

    const checkTruncation = () => {
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    };
    checkTruncation();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [message.message, isExpanded]);

  const tileColor = resolveTileColor(message.type, message.sentiment);
  const tileColorRgb = hexToRgbTriplet(tileColor);
  const hasPositiveCta = !!(message.cta_label && message.cta_url);
  const safeCtaUrl = message.cta_url
    ? normalizeProjectScopedCtaUrl(message.cta_url, currentProjectId)
    : null;

  const handleDismiss = () => {
    onDismissClick(message.id);
  };

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div
      className={styles.tile}
      style={{ '--gm-tile-color': tileColor, '--gm-tile-color-rgb': tileColorRgb }}
    >
      <div className={styles.messageArea}>
        <p
          ref={messageRef}
          className={isExpanded ? `${styles.message} ${styles.expanded}` : styles.message}
        >
          {message.message}
        </p>
        {isTruncated && (
          <button type="button" className={styles.toggleButton} onClick={handleToggleExpand}>
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>

      <div className={styles.actions}>
        {hasPositiveCta && (
          <a
            href={safeCtaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaButton}
          >
            {message.cta_label}
          </a>
        )}
        <button
          type="button"
          className={styles.dismissButton}
          onClick={handleDismiss}
          aria-label="Dismiss message"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default MessageTile;
